import * as s from "../VideoShareStyled";
import EmailSelectComponent from "components/atoms/select/email/EmailSelectComponent";
import { Text, ValidateWord } from "components/atoms/text/Text";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import TinyButton from "components/atoms/buttons/TinyButton";
import { VideoShareViewResult } from "api/interfaces/videoshareInterface.interface";
import DownloadIcon from "assets/icons/video_download.svg";
import { useCallback, useEffect, useState } from "react";
import { useMutation } from "react-query";
import {
  createSnapShotEmail,
  createSnapShotEmailGuest,
} from "api/videoshareAPI";
import { blobToFile } from "components/atoms/image/CanvasUtil";
import { changeDateToFormat } from "utils/functions";
import moment from "moment";
import Input from "components/atoms/input/Input";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import TextArea from "components/atoms/input/TextArea";
import useApiError from "hook/useApiError";

type Props = {
  state?: VideoShareViewResult;
  capture: () => void;
  capturedBlob: Blob | undefined;
  capturedVtt: string | undefined;
  onModelSnapshot: () => void;
  videoId: string | undefined;
  isAuth?: boolean;
};

export default function Snapshot(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [imageUrl, setImageUrl] = useState<string>();
  const [sendUsers, setSendUsers] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [postContent, setPostContent] = useState("");

  const { notifySuccess, notifyError, lang, intlString } = useApiError();

  useEffect(() => {
    if (props.capturedBlob !== undefined) {
      const objectURL = window.URL.createObjectURL(props.capturedBlob);
      setImageUrl(objectURL);
    }
    return () => {
      if (imageUrl !== undefined) {
        window.URL.revokeObjectURL(imageUrl);
      }
    };
  }, [props.capturedBlob]);

  const onSelectUser = useCallback((user: string[]) => {
    if ((user || []).length === 0) return;
    setSendUsers(user);
  }, []);

  const onChangeEmail = useCallback((email: string) => {
    setSendUsers([email]);
  }, []);

  const mutationSnapShotSend = useMutation(createSnapShotEmail, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        notifySuccess(lang.label.common.notify.email.success);
        props.onModelSnapshot();
      } else {
        notifyError(lang.ServerErrorCode[121014]);
      }
    },
    onError: () => {
      notifyError(lang.ServerErrorCode[121014]);
    },
  });

  const mutationSnapShotSendGuest = useMutation(createSnapShotEmailGuest, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        notifySuccess(lang.label.common.notify.email.success);
        props.onModelSnapshot();
      } else {
        notifyError(lang.ServerErrorCode[121014]);
      }
    },
    onError: () => {
      notifyError(lang.ServerErrorCode[121014]);
    },
  });

  const onCaptureSend = useCallback(() => {
    setErrorMessage("");
    if (sendUsers.length === 0) {
      setErrorMessage(intlString(lang.validateMsg.videoShare.err.emptyUser));
      return;
    }

    if (props.videoId === undefined) {
      setErrorMessage(
        intlString(lang.validateMsg.videoShare.err.invalidVideoId)
      );
      return;
    }
    const formData = new FormData();

    if (props.capturedBlob !== undefined) {
      const uploadFile = blobToFile(
        props.capturedBlob,
        "snapshot_" + props.capturedVtt + ".png"
      );
      formData.append("snapshot", uploadFile);
      const json = JSON.stringify(sendUsers);
      const blob = new Blob([json], { type: "application/json" });
      formData.append("users", blob);

      const json2 = JSON.stringify(props.capturedVtt);
      const blob2 = new Blob([props.capturedVtt as string], {
        type: "application/text",
      });
      formData.append("snapshotTime", blob2);

      const json3 = JSON.stringify(postContent);
      const blob3 = new Blob([postContent], { type: "application/text" });
      formData.append("message", blob3);

      if (props.isAuth) {
        mutationSnapShotSend.mutate({
          form: formData,
          accountId: selectedAccount.accountId,
          videoId: props.videoId,
        });
      } else {
        mutationSnapShotSendGuest.mutate({
          form: formData,
          videoId: props.videoId,
        });
      }
    }
  }, [
    mutationSnapShotSend,
    mutationSnapShotSendGuest,
    props.capturedBlob,
    props.capturedVtt,
    props.isAuth,
    props.videoId,
    selectedAccount.accountId,
    sendUsers,
  ]);

  return (
    <SmallPopupTemplate
      title="Snapshot"
      subTitle="Download or share a still image from this video clip."
      onClick={onCaptureSend}
      onModal={props.onModelSnapshot}
      confirmLabel="Send"
      data-testid="videoshare-snapshot-small-popup-template-capture-send"
    >
      <s.SnapshotImage>
        {<img className="capture-image" src={imageUrl} alt="captured_image" />}
        <div className="snapshot-bar">
          {/* 시간 */}
          <span>{changeDateToFormat(moment())}</span>

          <TinyButton
            label="Save Image"
            onClickBtn={props.capture}
            icon={<img src={DownloadIcon} alt="capture_download" />}
            data-testid="videoshare-snapshot-tiny-button-capture"
          />
        </div>
      </s.SnapshotImage>
      <s.SendBox>
        <Text bold>Send Snapshot</Text>
        {props.isAuth ? (
          <EmailSelectComponent
            //placeholder="Select User or Enter Email Address"
            onChange={onSelectUser}
            isVs
            data-testid="videoshare-snapshot-email-add-select-select-user"
          />
        ) : (
          <Input
            placeholder="Please enter an email"
            pattern="[A-Za-z]+"
            maxLength={50}
            onChange={(e) => onChangeEmail(e.currentTarget.value)}
            data-testid="videoshare-snapshot-input-change-email"
          />
        )}

        <Text bold>Message</Text>
        <TextArea
          placeholder="Please enter a message."
          rows={4}
          value={postContent}
          onChange={(e) => setPostContent(e.target.value)}
          data-testid="videoshare-snapshot-textarea-set-post-content"
        />
      </s.SendBox>
      <ValidateWord data-testid="videoshare-snapshot-validate-email">
        {errorMessage}
      </ValidateWord>
    </SmallPopupTemplate>
  );
}
